/**
 * Global route middleware that destroys GTM and Optimizely when navigating to
 * payment pages and re-enables them when leaving.
 *
 * The Nitro csp-nonce plugin strips the GTM <script> tag from server-rendered
 * HTML on direct loads of payment routes. The Optimizely plugin guards its own
 * initial load. This middleware handles the SPA navigation case where either
 * script is already running from a previous page visit.
 *
 * For GTM: simply calling gtm.enable(false) is insufficient — the already-loaded
 * runtime may still call eval() for pending operations, violating the CSP.
 * For Optimizely: the script uses eval() internally and must be removed from the
 * DOM and window scope to prevent CSP violations on payment pages.
 *
 * On leaving a payment page: GTM re-injects via gtm.enable(true); Optimizely
 * re-initialises via $optimizelyClient.init().
 */
import { PAYMENT_STEP } from '~/constants/application/health-insurance-v2'

export const PAYMENT_ROUTE_PREFIXES = [
  '/health-insurance/application/payment',
  `/health-insurance/applicationV2/${PAYMENT_STEP}`
] as const

export function isPaymentRoute (path: string): boolean {
  return PAYMENT_ROUTE_PREFIXES.some(prefix => path.startsWith(prefix))
}

/**
 * Remove all GTM-related script elements from the DOM and clean up
 * window-level GTM state so no residual eval() calls can fire.
 *
 * window.dataLayer is intentionally preserved — clearing it would lose
 * queued tracking events that GTM should pick up when re-enabled.
 */
export function destroyGtmRuntime (): void {
  const win = window as unknown as {
    google_tag_manager?: unknown
    gtag?: unknown
    dataLayer?: unknown[]
  }

  document.querySelectorAll('script[src*="googletagmanager.com"]').forEach(el => el.remove())
  document.querySelectorAll('iframe[src*="googletagmanager.com"]').forEach(el => el.remove())
  delete win.google_tag_manager
  delete win.gtag

  // Reset dataLayer to break any GTM-patched push handlers from previous pages.
  // We intentionally drop queued marketing events on payment routes.
  win.dataLayer = []
}

/**
 * Remove the Optimizely script from the DOM and delete its window-level state
 * so no residual eval() calls can fire on payment pages.
 *
 * window.optimizely is deleted (not just disabled) because Optimizely has no
 * first-class disable API and continues to call eval() while present.
 */
export function destroyOptimizelyRuntime (): void {
  document.querySelectorAll('script#optimizely-script').forEach(el => el.remove())
  delete (window as unknown as Record<string, unknown>).optimizely
}

export default defineNuxtRouteMiddleware((to, from) => {
  if (import.meta.server) return

  const gtm = useGtm()
  const toPayment = isPaymentRoute(to.path)
  const fromPayment = isPaymentRoute(from.path)

  if (toPayment && !fromPayment) {
    // The payment-page CSP header is only attached to server-rendered HTML
    // by the csp-nonce Nitro plugin. SPA navigation never hits the server,
    // so entering a payment route via SPA would leave the user on the
    // previous (CSP-free) document. Force a full page load so the browser
    // fetches a fresh response that includes the per-request CSP header.
    if (typeof window !== 'undefined') {
      window.location.assign(to.fullPath)
      return abortNavigation()
    }

    // Defensive fallback for non-browser environments (SSR, tests): tear
    // down GTM and Optimizely in place since no document swap will occur.
    if (gtm) {
      gtm.enable(false)
    } else {
      // eslint-disable-next-line no-console
      console.warn(
        '[gtm-payment-guard] GTM instance unavailable on payment entry; applying hard runtime teardown only.',
        { to: to.path, from: from.path }
      )
    }

    destroyGtmRuntime()
    destroyOptimizelyRuntime()
  } else if (!toPayment && fromPayment) {
    console.log(`[gtm-payment-guard] Navigating away from payment route: ${from.path} → ${to.path}`)
    // The payment document was served with a per-request CSP header that the
    // browser binds to that document for its entire lifetime. The only way to
    // shed that policy on exit is to replace the document with a fresh one,
    // so we force a full page reload here. After reload, GTM and Optimizely
    // re-initialise via their normal plugin boot path.
    if (typeof window !== 'undefined') {
      window.location.assign(to.fullPath)
      return abortNavigation()
    }

    // Defensive fallback for non-browser environments (SSR, tests): re-enable
    // GTM and Optimizely in place since no document swap will occur.
    if (gtm) {
      gtm.enable(true)
    } else {
      // eslint-disable-next-line no-console
      console.warn(
        '[gtm-payment-guard] GTM instance unavailable on payment exit; GTM re-enable skipped.',
        { to: to.path, from: from.path }
      )
    }

    const { $optimizelyClient } = useNuxtApp()
    if ($optimizelyClient && typeof ($optimizelyClient as { init: () => void }).init === 'function') {
      ;($optimizelyClient as { init: () => void }).init()
    }
  }
})
